import {
  NJButton,
  NJHeading,
  NJIcon,
  NJInlineMessage,
  NJSpinner,
  NJTag,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { MarkdownMessage } from "@/features/chat/MarkdownMessage";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentUser } from "@/api/useCurrentUser";
import { BackButton } from "@/components/BackButton";
import { MetaTagList } from "@/components/store/MetaTagList";
import { SessionListPanel } from "@/features/sessions/SessionListPanel";
import { useCreateSession } from "@/features/sessions/useSessions";
import { useCancelAccessRequest, useRequestAccess } from "./useAccessRequests";
import { useAgent } from "./useAgents";

const ACCESS_LABELS: Record<string, string> = {
  granted: "Granted",
  pending: "Pending",
  restricted: "Restricted",
};

function accessTagVariant(access: string): "green" | "orange" | "grey" {
  if (access === "granted") return "green";
  if (access === "pending") return "orange";
  return "grey";
}

/** Agent detail — access status, contextual actions, and (when granted) the sessions panel. */
export function AgentDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading, isError } = useAgent(slug);
  const { data: currentUser } = useCurrentUser();
  const isAdmin = (currentUser?.groups ?? []).includes("app-admin");
  const requestAccess = useRequestAccess();
  const cancelAccessRequest = useCancelAccessRequest();
  const createSession = useCreateSession();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <NJSpinner aria-label="Loading the agent" />
      </div>
    );
  }

  if (isError || !agent) {
    return <NJInlineMessage variant="error">Agent not found.</NJInlineMessage>;
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <BackButton to="/agents" label="Back to Agent Lib" />
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-nj-neutral-subtle-default pb-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-nj-brand-secondary-default text-nj-brand-solid-default">
            <NJIcon name="storefront" />
          </span>
          <div className="flex flex-col">
            <NJHeading as="h1" scale="lg">
              {agent.name}
            </NJHeading>
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              {ACCESS_LABELS[agent.access] ?? agent.access}
            </NJText>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {agent.access === "granted" && !isAdmin && (
            <NJButton
              label="Start session"
              icon="open_in_new"
              disabled={createSession.isPending}
              onClick={() =>
                createSession.mutate(agent.slug, {
                  onSuccess: (session) => navigate(`/chat/${session.id}`),
                })
              }
            />
          )}
          {agent.access === "restricted" && (
            <NJButton
              label="Request access"
              disabled={requestAccess.isPending}
              onClick={() => requestAccess.mutate(agent.slug)}
            />
          )}
          {agent.access === "pending" && (
            <>
              <NJButton label="Request pending" emphasis="subtle" disabled />
              <NJButton
                label="Cancel request"
                emphasis="subtle"
                disabled={cancelAccessRequest.isPending}
                onClick={() => cancelAccessRequest.mutate(agent.slug)}
              />
            </>
          )}
        </div>
      </header>

      <article className="mx-auto flex w-full max-w-[1240px] flex-col gap-4 border border-nj-neutral-subtle-default bg-nj-neutral-primary-default p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <NJHeading as="h2" scale="md">
            {agent.name}
          </NJHeading>
          <NJTag
            label={ACCESS_LABELS[agent.access] ?? agent.access}
            variant={accessTagVariant(agent.access)}
          />
        </div>

        {(agent.useCases ?? []).length > 0 && (
          <MetaTagList label="Use cases" items={agent.useCases ?? []} />
        )}

        {agent.fullDescription ? (
          <MarkdownMessage content={agent.fullDescription} />
        ) : (
          <NJText
            as="p"
            scale="sm"
            className="text-nj-neutral-secondary-default"
          >
            {agent.description || "No description provided."}
          </NJText>
        )}

        <div className="flex flex-col gap-0.5">
          <NJText
            as="span"
            scale="xs"
            className="text-nj-neutral-secondary-default"
          >
            Maintained by <strong>{agent.maintainer}</strong>
          </NJText>
          {agent.createdAt && (
            <NJText
              as="span"
              scale="xs"
              className="text-nj-neutral-secondary-default"
            >
              Added {new Date(agent.createdAt).toLocaleDateString("en-GB")}
            </NJText>
          )}
        </div>
      </article>

      {agent.access === "granted" && !isAdmin && (
        <section className="mx-auto flex w-full max-w-[1240px] flex-col gap-2">
          <NJHeading as="h2" scale="sm">
            Sessions
          </NJHeading>
          <SessionListPanel agentId={agent.slug} />
        </section>
      )}
    </div>
  );
}
